import { useEffect, useRef, useState } from "react";
import { launchTool } from "../lib/pwa";

/**
 * Persistent horizontal "command bar" for a personal terminal — styled like a
 * desktop app menu bar (Word/Excel). The terminal title sits on the left, then
 * one menu per category; clicking a menu opens a dropdown of that category's
 * tools to launch. Sticks to the top of the page as you scroll.
 *
 * Menu-bar behavior: click toggles a menu; once any menu is open, hovering a
 * sibling switches to it (classic desktop menu bar). Click-outside / Esc close.
 *
 * Stacks are scoped to a single asset class, so a category's tools are listed
 * flat (no asset/sub-function collisions). Large categories still get inline
 * sub-sections (grouped by category_2) inside the same dropdown — no side
 * flyouts — so it works identically in the narrow extension column.
 */

// Group a category's tools into inline sub-sections by category_2 — but ONLY
// using sub-labels that are genuine sub-functions. A category_2 value that is
// itself a top-level category (e.g. "Screeners", which has its own menu) is
// NOT used as a sub-header (that read as a contradiction); those tools fall
// into an unlabeled group instead. Needs 2+ real sub-labels to bother.
function subSections(tools, categoryVocab) {
  if (!tools || tools.length === 0) return null;
  const top = new Set(categoryVocab || []);
  const buckets = {};
  const order = [];
  const flat = [];
  for (const t of tools) {
    const k = t.category_2;
    if (k && k !== t.category && !top.has(k)) {
      if (!buckets[k]) {
        buckets[k] = [];
        order.push(k);
      }
      buckets[k].push(t);
    } else {
      flat.push(t);
    }
  }
  if (order.length < 2) return null;
  order.sort((a, b) => buckets[b].length - buckets[a].length || a.localeCompare(b));
  const out = order.map((k) => ({ label: k, tools: buckets[k] }));
  if (flat.length) out.push({ label: null, tools: flat });
  return out;
}

function ToolItem({ t, openMode = "newtab" }) {
  // Route every launch through launchTool so the destination window is tagged
  // as a TerminalValue tool window (window.name = "tv_ws_…") and the browser
  // extension injects the menu bar there — for ALL modes, including "newtab".
  // A real <a href> stays present for crawlers; modifier/middle clicks fall
  // through to native behavior (open in background, etc.).
  const onClick = (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return; // respect modifier opens
    e.preventDefault();
    launchTool(t.url, openMode);
  };
  return (
    <a
      href={t.url}
      target={openMode === "same" ? "_self" : "_blank"}
      rel="sponsored noopener noreferrer"
      onClick={onClick}
      className="tv-menulink block px-3 py-2 border-b border-black/10 last:border-b-0"
      data-testid={`terminal-menu-item-${t.id}`}
      title={`Launch ${t.title} ↗`}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-[12px] truncate">{t.title}</span>
        <span className="text-[11px] opacity-70 shrink-0">↗</span>
      </span>
      {t.key_feature && (
        <span className="block text-[11px] opacity-60 truncate mt-0.5">{t.key_feature}</span>
      )}
    </a>
  );
}

// Build dropdown sections for a category. Prefers the server's AI-named
// related-tool clusters (`clusters[cat]`); falls back to category_2 grouping.
function sectionsForCategory(cat, tools, clusters, categoryVocab) {
  const cl = clusters && clusters[cat];
  if (cl && cl.length) {
    const bySlug = {};
    tools.forEach((t) => {
      bySlug[t.slug] = t;
    });
    const used = new Set();
    const sections = [];
    for (const c of cl) {
      const ts = (c.slugs || []).map((s) => bySlug[s]).filter(Boolean);
      ts.forEach((t) => used.add(t.slug));
      if (ts.length) sections.push({ label: c.name || null, tools: ts });
    }
    const leftover = tools.filter((t) => !used.has(t.slug));
    if (leftover.length) sections.push({ label: null, tools: leftover });
    if (sections.filter((s) => s.label).length) return sections;
  }
  return subSections(tools, categoryVocab);
}

export default function TerminalMenuBar({ title, groups, onConcierge, leftSlot = null, rightSlot = null, categoryVocab = [], clusters = null, openMode = "newtab" }) {
  const [openCat, setOpenCat] = useState(null);
  const barRef = useRef(null);
  const [drop, setDrop] = useState({ top: 0, mobile: false });

  useEffect(() => {
    if (!openCat) return;
    const mobile =
      typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches;
    const rect = barRef.current?.getBoundingClientRect();
    setDrop({ top: rect ? Math.round(rect.bottom) : 0, mobile });
  }, [openCat]);

  useEffect(() => {
    const onDown = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) setOpenCat(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenCat(null);
    };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, []);

  return (
    <div
      ref={barRef}
      className="sticky top-0 z-40 bg-white text-black border-b border-black select-none"
      data-testid="terminal-menubar"
    >
      <div className="flex items-stretch flex-nowrap overflow-x-auto sm:flex-wrap sm:overflow-visible [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {leftSlot !== null ? (
          leftSlot
        ) : (
          <div className="px-3 py-2 font-bold text-[13px] whitespace-nowrap border-r border-black/15 shrink-0" data-testid="terminal-title">
            {">"}_ {title}
          </div>
        )}

        {groups.map((g) => {
          const isOpen = openCat === g.cat;
          return (
            <div key={g.cat} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setOpenCat(isOpen ? null : g.cat)}
                onMouseEnter={() => setOpenCat((c) => (c !== null ? g.cat : c))}
                className={`px-3 py-2 text-[12px] whitespace-nowrap transition-colors ${
                  isOpen ? "bg-[#1a73e8] text-white" : "hover:bg-black/[0.08]"
                }`}
                data-testid={`terminal-menu-${g.cat}`}
                aria-expanded={isOpen}
                aria-haspopup="true"
              >
                {g.cat}{" "}
                <span className={isOpen ? "opacity-60" : "opacity-40"}>▾</span>
                <span className="ml-1 opacity-40 text-[10px] tabular-nums">
                  {g.tools.length}
                </span>
              </button>

              {isOpen && (
                <div
                  className={`bg-white text-black border border-black z-50 max-h-[70vh] overflow-y-auto ${
                    drop.mobile
                      ? "fixed left-2 right-2"
                      : "absolute left-0 top-full min-w-[280px] max-w-[360px]"
                  }`}
                  style={drop.mobile ? { top: drop.top } : undefined}
                  data-testid={`terminal-dropdown-${g.cat}`}
                >
                  {(() => {
                    const subs = sectionsForCategory(g.cat, g.tools, clusters, categoryVocab);
                    if (!subs) return g.tools.map((t) => <ToolItem key={t.id} t={t} openMode={openMode} />);
                    return subs.map((s, i) => (
                      <div key={s.label || `_flat_${i}`}>
                        {s.label && (
                          <div
                            className="px-3 pt-2 pb-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-black bg-[#f2f2f2] border-y border-black/15"
                            data-testid={`terminal-subsection-${g.cat}-${s.label}`}
                          >
                            {s.label}
                          </div>
                        )}
                        {s.tools.map((t) => (
                          <ToolItem key={t.id} t={t} openMode={openMode} />
                        ))}
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
          );
        })}

        {onConcierge && (
          <button
            type="button"
            onClick={onConcierge}
            className="px-3 py-2 text-[12px] whitespace-nowrap border-l border-black/15 hover:bg-[#1a73e8] hover:text-white transition-colors shrink-0"
            data-testid="terminal-concierge-btn"
            title="Ask the concierge to find & add tools to this terminal"
          >
            + add tools
          </button>
        )}

        {rightSlot}
      </div>
    </div>
  );
}
